import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AlertTriangle, Download, ExternalLink, Filter, Loader2, Map as MapIcon, Pencil, Plus, RefreshCcw, Trash2, Upload } from 'lucide-react'

import { Button } from '../components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../components/ui/dialog'
import { EmptyState } from '../components/ui/empty-state'
import { Input } from '../components/ui/input'
import { MeasurementInput, MeasurementValue } from '../components/ui/measurement'
import { Textarea } from '../components/ui/textarea'
import { useToast } from '../components/ui/toast'
import { useAuth } from '../hooks/useAuth'
import { useMeasurements } from '../hooks/useMeasurements'
import { evaluateAllHikeDifficulties } from '../lib/api/hikeDifficulty'
import {
  createTour,
  deleteAllTours,
  deleteTour,
  deleteTourHeroImage,
  getTourReadiness,
  getTourOperators,
  ItineraryDay,
  recalculateTourTotals,
  regenerateAllTourThumbnails,
  regenerateTourThumbnails,
  TourReadiness,
  TourOperator,
  TourSummary,
  TourUpsertRequest,
  updateTour,
  uploadTourHeroImage
} from '../lib/api/tourOperators'
import { hikeInputToMetric, unitLabelFor, type MeasurementUnit } from '../lib/measurements'
import { formatHikeDifficulty, HIKE_DIFFICULTY_OPTIONS, parseHikeDifficulty, parseHikeDifficultyMode } from '../lib/hikeDifficulty'
import {
  DEFAULT_PLAN_GENERATION_PROFILE,
  TOUR_PLAN_GENERATION_PROFILES,
  parseTourPlanGenerationProfile,
  planGenerationProfileLabel,
  toTourPlanGenerationProfile
} from '../lib/planGenerationProfile'
import {
  addDay,
  createEmptyTourForm,
  DayFormState,
  mapTourToForm,
  MAX_ITINERARY_DAYS,
  removeDay,
  TourFormState,
  tourFormToPayload,
  updateDayField
} from '../utils/tourForm'
import { buildHeaderIndex, downloadCsv, makeGetValue, normalizeText, parseBooleanValue, parseCsvText, parseNumberOrNull, validateCsvSchema, withCsvSchemaColumns, withCsvSchemaRow } from '../utils/csv'

type FlattenedTour = TourSummary & { operatorName: string }
type SortDirection = 'asc' | 'desc'
type TourSortKey = 'name' | 'operatorName' | 'dayCount' | 'totalDistanceKm' | 'totalElevationGainMeters' | 'totalElevationLossMeters'
type TourSortConfig = { key: TourSortKey; direction: SortDirection }

type TourCsvKey =
  | 'OperatorName'
  | 'TourName'
  | 'Link'
  | 'Description'
  | 'Difficulty'
  | 'DifficultyLevel'
  | 'DifficultyMode'
  | 'PlanGenerationProfile'
  | 'PackWeight'
  | 'HighestPoint'
  | 'AboveEightThousandFeet'
  | 'SourceMeasurementUnit'
  | 'Country'
  | 'StateOrProvince'
  | 'GeographicRegion'
  | 'City'
  | 'ProtectedArea'
  | 'ItineraryKmMeters'

const TOUR_CSV_TYPE = 'Tours'
const TOUR_CSV_VERSION = 3

const TOUR_CSV_COLUMNS: TourCsvKey[] = [
  'OperatorName',
  'TourName',
  'Link',
  'Description',
  'DifficultyLevel',
  'DifficultyMode',
  'PlanGenerationProfile',
  'PackWeight',
  'HighestPoint',
  'AboveEightThousandFeet',
  'SourceMeasurementUnit',
  'Country',
  'StateOrProvince',
  'GeographicRegion',
  'City',
  'ProtectedArea',
  'ItineraryKmMeters'
]

const TOUR_HEADER_ALIASES: Record<TourCsvKey, string[]> = {
  OperatorName: ['operatorname', 'operator'],
  TourName: ['tourname', 'tour', 'name'],
  Link: ['link', 'url'],
  Description: ['description', 'tourdescription', 'summary'],
  Difficulty: ['difficulty'],
  DifficultyLevel: ['difficultylevel', 'difficultyenum', 'newdifficulty'],
  DifficultyMode: ['difficultymode', 'ratingmode'],
  PlanGenerationProfile: ['plangenerationprofile', 'trainingplanprofile', 'planprofile'],
  PackWeight: ['packweight', 'pack'],
  HighestPoint: ['highestpoint', 'highpoint', 'maxelevation'],
  AboveEightThousandFeet: ['aboveeightthousandfeet', 'above8kfeet', 'above8kft'],
  SourceMeasurementUnit: ['sourcemeasurementunit', 'measurementunit', 'sourceunit'],
  Country: ['country'],
  StateOrProvince: ['stateorprovince', 'state', 'province'],
  GeographicRegion: ['geographicregion', 'region'],
  City: ['city'],
  ProtectedArea: ['protectedarea', 'park', 'preserve'],
  ItineraryKmMeters: ['itinerarykmmeters', 'itinerarymetric', 'itinerary', 'days']
}

const TOUR_CSV_FALLBACK: Record<TourCsvKey, number> = {
  OperatorName: 0,
  TourName: 1,
  Link: 2,
  Description: -1,
  Difficulty: -1,
  DifficultyLevel: -1,
  DifficultyMode: -1,
  PlanGenerationProfile: -1,
  PackWeight: -1,
  HighestPoint: -1,
  AboveEightThousandFeet: -1,
  SourceMeasurementUnit: -1,
  Country: -1,
  StateOrProvince: -1,
  GeographicRegion: -1,
  City: -1,
  ProtectedArea: -1,
  ItineraryKmMeters: 3
}

const serializeItinerary = (days: ItineraryDay[]): string =>
  days.map((d) => `${d.dayNumber}:${d.distanceKm ?? 0}/${d.elevationGainMeters ?? 0}/${d.elevationLossMeters ?? 0}`).join('|')

const parseItinerary = (value: string, unit: 'Metric' | 'Imperial' = 'Metric'): ItineraryDay[] => {
  if (!value.trim()) return []
  return value.split('|').map((segment) => {
    const [dayStr, metricsStr] = segment.split(':')
    const [dist, gain, loss] = (metricsStr ?? '').split('/')
    const distance = parseNumberOrNull(dist ?? '') ?? 0
    const gainValue = parseNumberOrNull(gain ?? '') ?? 0
    const lossValue = parseNumberOrNull(loss ?? '') ?? 0
    return {
      dayNumber: parseInt(dayStr) || 1,
      distanceKm: hikeInputToMetric(distance, 'distance', unit),
      elevationGainMeters: hikeInputToMetric(gainValue, 'elevation', unit),
      elevationLossMeters: hikeInputToMetric(lossValue, 'elevation', unit)
    }
  })
}

const sumItinerary = (days: ItineraryDay[]) => {
  let distanceKm = 0
  let elevationGainMeters = 0
  let elevationLossMeters = 0
  let hasAny = false

  for (const day of days) {
    if (day.distanceKm != null) {
      distanceKm += day.distanceKm
      hasAny = true
    }
    if (day.elevationGainMeters != null) {
      elevationGainMeters += day.elevationGainMeters
      hasAny = true
    }
    if (day.elevationLossMeters != null) {
      elevationLossMeters += day.elevationLossMeters
      hasAny = true
    }
  }

  if (!hasAny) {
    return {
      distanceKm: null,
      elevationGainMeters: null,
      elevationLossMeters: null
    }
  }

  return {
    distanceKm: distanceKm || null,
    elevationGainMeters: elevationGainMeters || null,
    elevationLossMeters: elevationLossMeters || null
  }
}

const formMetricString = (value: number | null) => (value == null ? '' : value.toString())

const formatBooleanCsv = (value?: boolean | null) => {
  if (value === true) return 'TRUE'
  if (value === false) return 'FALSE'
  return ''
}

const parseOptionalBooleanCell = (value: string) => {
  if (!value.trim()) return null
  return parseBooleanValue(value)
}

export const TourManagementPage = () => {
  const { user } = useAuth()
  const { addToast } = useToast()
  const measurements = useMeasurements()

  const [operators, setOperators] = useState<TourOperator[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filterOperatorId, setFilterOperatorId] = useState<string>(() => (user?.role === 'TourAdmin' && user.operatorId) ? user.operatorId : 'all')
  const [selectedTourId, setSelectedTourId] = useState<string | null>(null)
  const [editForm, setEditForm] = useState<TourFormState | null>(null)
  const [newTourForm, setNewTourForm] = useState<TourFormState>(() => createEmptyTourForm(measurements.unit))
  const [newTourOperatorId, setNewTourOperatorId] = useState<string | null>(user?.role === 'TourAdmin' ? user.operatorId ?? null : null)
  const [savingTourId, setSavingTourId] = useState<string | null>(null)
  const [creatingTour, setCreatingTour] = useState(false)
  const [deletingTourId, setDeletingTourId] = useState<string | null>(null)
  const [deletingAllTours, setDeletingAllTours] = useState(false)
  const [uploadingHeroImage, setUploadingHeroImage] = useState(false)
  const [deletingHeroImageUrl, setDeletingHeroImageUrl] = useState<string | null>(null)
  const [regeneratingThumbnails, setRegeneratingThumbnails] = useState(false)
  const [regeneratingAllThumbnails, setRegeneratingAllThumbnails] = useState(false)
  const [recalculatingTotals, setRecalculatingTotals] = useState(false)
  const [evaluatingHikeDifficulties, setEvaluatingHikeDifficulties] = useState(false)
  const [tourReadiness, setTourReadiness] = useState<Record<string, TourReadiness>>({})
  const [readinessLoading, setReadinessLoading] = useState(false)
  const [readinessError, setReadinessError] = useState<string | null>(null)
  const [isCsvImporting, setIsCsvImporting] = useState(false)
  const [isCsvExporting, setIsCsvExporting] = useState(false)
  const [sortConfig, setSortConfig] = useState<TourSortConfig>({ key: 'name', direction: 'asc' })
  const csvInputRef = useRef<HTMLInputElement | null>(null)

  const isElevated = user?.role === 'Admin' || user?.role === 'Editor'
  const isTourAdmin = user?.role === 'TourAdmin'
  const canEvaluateHikeDifficulties = user?.role === 'Admin'

  const loadOperators = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await getTourOperators()
      setOperators(data)
    } catch (err) {
      console.error('Failed to load tour operators', err)
      setError('Unable to load tours right now.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadOperators()
  }, [loadOperators])

  const loadTourReadiness = useCallback(async () => {
    setReadinessLoading(true)
    setReadinessError(null)
    try {
      const data = await getTourReadiness(filterOperatorId === 'all' ? null : filterOperatorId)
      setTourReadiness(Object.fromEntries(data.map((item) => [item.tourId, item])))
    } catch (err) {
      console.error('Failed to load tour readiness', err)
      setReadinessError('Unable to load Trip Readiness right now.')
    } finally {
      setReadinessLoading(false)
    }
  }, [filterOperatorId])

  useEffect(() => {
    void loadTourReadiness()
  }, [loadTourReadiness])

  useEffect(() => {
    if (isElevated && !newTourOperatorId && operators.length > 0) {
      setNewTourOperatorId(operators[0].id)
    }
  }, [isElevated, newTourOperatorId, operators])

  useEffect(() => {
    if (isTourAdmin && user?.operatorId) {
      setNewTourOperatorId(user.operatorId)
      setFilterOperatorId((current) => (current === 'all' ? user.operatorId! : current))
    }
  }, [isTourAdmin, user?.operatorId])

  const flattenedTours = useMemo<FlattenedTour[]>(() => {
    return operators.flatMap((operator) =>
      operator.tours.map((tour) => ({
        ...tour,
        operatorName: operator.name
      }))
    )
  }, [operators])

  const filteredTours = useMemo(() => {
    if (filterOperatorId === 'all') {
      return flattenedTours
    }
    return flattenedTours.filter((tour) => tour.operatorId === filterOperatorId)
  }, [flattenedTours, filterOperatorId])

  const sortedTours = useMemo(() => {
    const tours = [...filteredTours]
    const { key, direction } = sortConfig

    return tours.sort((a, b) => {
      let cmp = 0
      switch (key) {
        case 'name':
          cmp = a.name.localeCompare(b.name)
          break
        case 'operatorName':
          cmp = a.operatorName.localeCompare(b.operatorName)
          break
        case 'dayCount':
          cmp = a.itinerary.length - b.itinerary.length
          break
        case 'totalDistanceKm':
          cmp = (a.totalDistanceKm ?? 0) - (b.totalDistanceKm ?? 0)
          break
        case 'totalElevationGainMeters':
          cmp = (a.totalElevationGainMeters ?? 0) - (b.totalElevationGainMeters ?? 0)
          break
        case 'totalElevationLossMeters':
          cmp = (a.totalElevationLossMeters ?? 0) - (b.totalElevationLossMeters ?? 0)
          break
      }

      return direction === 'asc' ? cmp : -cmp
    })
  }, [filteredTours, sortConfig])

  const tourLookup = useMemo(() => new Map(flattenedTours.map((tour) => [tour.id, tour])), [flattenedTours])
  const selectedTour = selectedTourId ? tourLookup.get(selectedTourId) : null
  const editComputedTotals = useMemo(
    () => (editForm ? sumItinerary(tourFormToPayload(editForm).itinerary) : null),
    [editForm]
  )
  const newTourComputedTotals = useMemo(
    () => sumItinerary(tourFormToPayload(newTourForm).itinerary),
    [newTourForm]
  )

  const canManageTour = useCallback((operatorId: string) => {
    if (isElevated) {
      return true
    }
    return isTourAdmin && user?.operatorId === operatorId
  }, [isElevated, isTourAdmin, user?.operatorId])

  const handleSelectTour = (tourId: string) => {
    const tour = tourLookup.get(tourId)
    if (!tour) {
      return
    }
    if (!canManageTour(tour.operatorId)) {
      addToast({ variant: 'error', title: 'Restricted', description: 'You can only edit tours linked to your operator.' })
      return
    }
    setSelectedTourId(tourId)
    setEditForm(mapTourToForm(tour, measurements.unit))
  }

  const closeEditDialog = useCallback(() => {
    setSelectedTourId(null)
    setEditForm(null)
  }, [])

  const requestSort = (key: TourSortKey) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { ...prev, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
      }
      return { key, direction: 'asc' }
    })
  }

  const renderSortIndicator = (key: TourSortKey) => {
    if (sortConfig.key !== key) {
      return <span className="text-muted-foreground/50" aria-hidden>⇅</span>
    }
    return sortConfig.direction === 'asc' ? <span aria-hidden>↑</span> : <span aria-hidden>↓</span>
  }

  const handleSaveTour = async () => {
    if (!selectedTourId || !editForm) {
      addToast({ variant: 'error', title: 'Select a tour', description: 'Choose a tour before saving changes.' })
      return
    }

    const existing = tourLookup.get(selectedTourId)
    if (!existing || !canManageTour(existing.operatorId)) {
      addToast({ variant: 'error', title: 'No permission', description: 'You cannot update that tour.' })
      return
    }

    if (!editForm.name.trim()) {
      addToast({ variant: 'error', title: 'Tour name required', description: 'Add a name before saving changes.' })
      return
    }

    const payload = tourFormToPayload(editForm)
    setSavingTourId(selectedTourId)
    try {
      const updated = await updateTour(selectedTourId, payload)
      setOperators((prev) =>
        prev.map((operator) => {
          if (operator.id !== updated.operatorId) {
            return operator
          }
          return {
            ...operator,
            tours: operator.tours.map((tour) => (tour.id === updated.id ? updated : tour))
          }
        })
      )
      setEditForm(mapTourToForm(updated, measurements.unit))
      addToast({ variant: 'success', title: 'Tour updated', description: `${updated.name} saved successfully.` })
    } catch (err) {
      console.error('Failed to update tour', err)
      addToast({ variant: 'error', title: 'Unable to save', description: 'Try again in a moment.' })
    } finally {
      setSavingTourId(null)
    }
  }

  const handleDeleteTour = async (tourId: string) => {
    const tour = tourLookup.get(tourId)
    if (!tour || !canManageTour(tour.operatorId)) {
      addToast({ variant: 'error', title: 'No permission', description: 'You cannot delete that tour.' })
      return
    }
    if (!window.confirm(`Delete ${tour.name}? This cannot be undone.`)) {
      return
    }

    setDeletingTourId(tourId)
    try {
      await deleteTour(tourId)
      setOperators((prev) =>
        prev.map((operator) => {
          if (operator.id !== tour.operatorId) {
            return operator
          }
          return {
            ...operator,
            tours: operator.tours.filter((item) => item.id !== tourId)
          }
        })
      )
      if (selectedTourId === tourId) {
        closeEditDialog()
      }
      addToast({ variant: 'success', title: 'Tour deleted', description: `${tour.name} has been removed.` })
    } catch (err) {
      console.error('Failed to delete tour', err)
      addToast({ variant: 'error', title: 'Unable to delete', description: 'Please try again.' })
    } finally {
      setDeletingTourId(null)
    }
  }

  const handleCreateTour = async () => {
    const operatorId = isElevated ? newTourOperatorId : user?.operatorId
    if (!operatorId) {
      addToast({ variant: 'error', title: 'Operator required', description: 'Select an operator before creating a tour.' })
      return
    }
    if (!newTourForm.name.trim()) {
      addToast({ variant: 'error', title: 'Tour name required', description: 'Add a name for the new tour.' })
      return
    }

    const payload = tourFormToPayload(newTourForm)
    setCreatingTour(true)
    try {
      const created = await createTour(operatorId, payload)
      setOperators((prev) =>
        prev.map((operator) => {
          if (operator.id !== operatorId) {
            return operator
          }
          return { ...operator, tours: [...operator.tours, created] }
        })
      )
      setNewTourForm(createEmptyTourForm(measurements.unit))
      addToast({ variant: 'success', title: 'Tour created', description: `${created.name} added successfully.` })
    } catch (err) {
      console.error('Failed to create tour', err)
      addToast({ variant: 'error', title: 'Unable to create tour', description: 'Try again later.' })
    } finally {
      setCreatingTour(false)
    }
  }

  const handleEditFormChange = (index: number, field: keyof DayFormState, value: string) => {
    setEditForm((prev) => (prev ? updateDayField(prev, index, field, value) : prev))
  }

  const handleNewFormChange = (index: number, field: keyof DayFormState, value: string) => {
    setNewTourForm((prev) => updateDayField(prev, index, field, value))
  }

  const addDayToEditForm = () => setEditForm((prev) => (prev ? addDay(prev) : prev))
  const addDayToNewForm = () => setNewTourForm((prev) => addDay(prev))
  const removeDayFromEditForm = (index: number) => setEditForm((prev) => (prev ? removeDay(prev, index) : prev))
  const removeDayFromNewForm = (index: number) => setNewTourForm((prev) => removeDay(prev, index))

  const handleUploadHeroImage = async (file: File) => {
    if (!selectedTourId) return
    setUploadingHeroImage(true)
    try {
      const updated = await uploadTourHeroImage(selectedTourId, file)
      setOperators((prev) =>
        prev.map((operator) => {
          if (operator.id !== updated.operatorId) return operator
          return {
            ...operator,
            tours: operator.tours.map((tour) => (tour.id === updated.id ? updated : tour))
          }
        })
      )
      addToast({ variant: 'success', title: 'Image uploaded', description: 'Hero image added successfully.' })
    } catch (err) {
      console.error('Failed to upload hero image', err)
      addToast({ variant: 'error', title: 'Upload failed', description: 'Unable to upload hero image. Please try again.' })
    } finally {
      setUploadingHeroImage(false)
    }
  }

  const handleDeleteHeroImage = async (imageUrl: string) => {
    if (!selectedTourId) return
    if (!window.confirm('Delete this hero image? This cannot be undone.')) return
    setDeletingHeroImageUrl(imageUrl)
    try {
      const updated = await deleteTourHeroImage(selectedTourId, imageUrl)
      setOperators((prev) =>
        prev.map((operator) => {
          if (operator.id !== updated.operatorId) return operator
          return {
            ...operator,
            tours: operator.tours.map((tour) => (tour.id === updated.id ? updated : tour))
          }
        })
      )
      addToast({ variant: 'success', title: 'Image deleted', description: 'Hero image removed successfully.' })
    } catch (err) {
      console.error('Failed to delete hero image', err)
      addToast({ variant: 'error', title: 'Delete failed', description: 'Unable to delete hero image. Please try again.' })
    } finally {
      setDeletingHeroImageUrl(null)
    }
  }

  const triggerCsvImport = () => {
    if (csvInputRef.current) {
      csvInputRef.current.value = ''
      csvInputRef.current.click()
    }
  }

  const handleExportCsv = () => {
    setIsCsvExporting(true)
    try {
      const rows = flattenedTours.map((tour) => [
        tour.operatorName,
        tour.name,
        tour.link ?? '',
        tour.description ?? '',
        parseHikeDifficulty(tour.difficultyLevel) ?? '',
        tour.difficultyMode ?? 'Legacy',
        tour.planGenerationProfile ?? DEFAULT_PLAN_GENERATION_PROFILE,
        tour.packWeight ?? '',
        tour.highestPoint ?? '',
        formatBooleanCsv(tour.aboveEightThousandFeet),
        tour.sourceMeasurementUnit ?? 'Metric',
        tour.country ?? '',
        tour.stateOrProvince ?? '',
        tour.geographicRegion ?? '',
        tour.city ?? '',
        tour.protectedArea ?? '',
        serializeItinerary(tour.itinerary)
      ])
      const timestamp = new Date().toISOString().slice(0, 10)
      downloadCsv(
        withCsvSchemaColumns(TOUR_CSV_COLUMNS),
        rows.map((row) => withCsvSchemaRow(TOUR_CSV_TYPE, TOUR_CSV_VERSION, row)),
        `hikestrong-tours-${timestamp}.csv`
      )
      addToast({ title: 'CSV exported', description: `Exported ${rows.length} tours.` })
    } catch (err) {
      console.error('Failed to export tours CSV', err)
      addToast({ title: 'Export failed', description: 'Could not export the CSV file.' })
    } finally {
      setIsCsvExporting(false)
    }
  }

  const handleImportCsv = async (file: File) => {
    setIsCsvImporting(true)
    try {
      const text = await file.text()
      // Parse the whole document so quoted multi-line fields round-trip.
      const rows = parseCsvText(text)
      if (rows.length === 0) throw new Error('CSV file is empty.')

      const header = rows[0].map((v) => v.trim())
      const normalizedHeader = header.map(normalizeText)
      const hasHeader =
        normalizedHeader.some((c) => c === 'tourname' || c === 'name') &&
        normalizedHeader.some((c) => c === 'operatorname' || c === 'operator')

      const dataRows = hasHeader ? rows.slice(1) : rows
      if (dataRows.length === 0) throw new Error('CSV appears to contain only headers.')

      const schemaResult = validateCsvSchema(header, dataRows, {
        expectedType: TOUR_CSV_TYPE,
        currentVersion: TOUR_CSV_VERSION,
        entityName: 'Tours'
      })
      if (schemaResult.warning) {
        addToast({ title: 'Legacy CSV format', description: schemaResult.warning, duration: 9000 })
      }

      const headerIndex = hasHeader ? buildHeaderIndex(header, TOUR_HEADER_ALIASES) : null
      const getValue = makeGetValue(headerIndex, TOUR_CSV_FALLBACK)
      const getExplicitValue = (row: string[], aliases: string[]) => {
        if (!hasHeader) return ''
        const index = aliases
          .map(normalizeText)
          .map((alias) => normalizedHeader.indexOf(alias))
          .find((candidate) => candidate >= 0)
        return index === undefined ? '' : (row[index] ?? '').trim()
      }

      const operatorByName = new Map(operators.map((op) => [normalizeText(op.name), op]))
      const tourLookupByKey = new Map(
        flattenedTours.map((t) => [`${normalizeText(t.operatorName)}::${normalizeText(t.name)}`, t])
      )

      let created = 0
      let updated = 0
      const errors: string[] = []

      for (const row of dataRows) {
        const operatorName = getValue(row, 'OperatorName')
        const tourName = getValue(row, 'TourName')
        if (!operatorName || !tourName) continue

        const operator = operatorByName.get(normalizeText(operatorName))
        if (!operator) {
          errors.push(`${tourName} (unknown operator: ${operatorName})`)
          continue
        }

        const imperialItinerary = getExplicitValue(row, ['ItineraryMilesFeet', 'ItineraryMiFt'])
        const sourceMeasurementUnitCell = getValue(row, 'SourceMeasurementUnit').trim()
        const normalizedSourceMeasurementUnit = sourceMeasurementUnitCell.toLowerCase()
        const sourceMeasurementUnit = normalizedSourceMeasurementUnit === 'imperial' || normalizedSourceMeasurementUnit === 'metric'
          ? (normalizedSourceMeasurementUnit === 'imperial' ? 'Imperial' : 'Metric')
          : imperialItinerary
            ? 'Imperial'
            : 'Metric'
        if (sourceMeasurementUnitCell && normalizedSourceMeasurementUnit !== 'imperial' && normalizedSourceMeasurementUnit !== 'metric') {
          errors.push(`${tourName} (invalid source measurement unit)`)
          continue
        }
        const existingKey = `${normalizeText(operatorName)}::${normalizeText(tourName)}`
        const existing = tourLookupByKey.get(existingKey)
        const difficultyLevelRaw = getValue(row, 'DifficultyLevel').trim()
        const importedDifficultyLevel = parseHikeDifficulty(difficultyLevelRaw)
        if (difficultyLevelRaw && !importedDifficultyLevel) {
          errors.push(`${tourName} (invalid difficulty level)`)
          continue
        }
        const difficultyModeRaw = getValue(row, 'DifficultyMode').trim()
        const importedDifficultyMode = parseHikeDifficultyMode(difficultyModeRaw)
        if (difficultyModeRaw && !importedDifficultyMode) {
          errors.push(`${tourName} (invalid difficulty mode)`)
          continue
        }
        const difficultyMode = importedDifficultyMode ?? 'Calculated'
        if (difficultyMode === 'Manual' && !importedDifficultyLevel) {
          errors.push(`${tourName} (manual difficulty requires a difficulty level)`)
          continue
        }
        const planGenerationProfileRaw = getValue(row, 'PlanGenerationProfile').trim()
        const importedPlanGenerationProfile = parseTourPlanGenerationProfile(planGenerationProfileRaw)
        if (planGenerationProfileRaw && !importedPlanGenerationProfile) {
          errors.push(`${tourName} (invalid plan generation profile)`)
          continue
        }
        const payload: TourUpsertRequest = {
          name: tourName,
          link: getValue(row, 'Link') || null,
          description: getValue(row, 'Description') || null,
          difficulty: getValue(row, 'Difficulty') || existing?.difficulty || null,
          difficultyLevel: difficultyMode === 'Calculated' ? null : importedDifficultyLevel,
          difficultyMode,
          planGenerationProfile: importedPlanGenerationProfile ?? toTourPlanGenerationProfile(existing?.planGenerationProfile),
          packWeight: getValue(row, 'PackWeight') || null,
          highestPoint: getValue(row, 'HighestPoint') || null,
          aboveEightThousandFeet: parseOptionalBooleanCell(getValue(row, 'AboveEightThousandFeet')),
          sourceMeasurementUnit,
          country: getValue(row, 'Country') || null,
          stateOrProvince: getValue(row, 'StateOrProvince') || null,
          geographicRegion: getValue(row, 'GeographicRegion') || null,
          city: getValue(row, 'City') || null,
          protectedArea: getValue(row, 'ProtectedArea') || null,
          itinerary: imperialItinerary
            ? parseItinerary(imperialItinerary, 'Imperial')
            : parseItinerary(getValue(row, 'ItineraryKmMeters'))
        }

        try {
          if (existing) {
            await updateTour(existing.id, payload)
            updated += 1
          } else {
            await createTour(operator.id, payload)
            created += 1
          }
        } catch (err) {
          console.error('Failed to import tour', tourName, err)
          errors.push(tourName)
        }
      }

      if (created > 0 || updated > 0) {
        await loadOperators()
      }

      const details = []
      if (created > 0) details.push(`${created} created`)
      if (updated > 0) details.push(`${updated} updated`)
      if (errors.length > 0) details.push(`${errors.length} failed`)

      addToast({
        title: errors.length > 0 ? 'Import completed with errors' : 'Import complete',
        description: details.length > 0 ? details.join(', ') + '.' : 'No tours imported.'
      })
    } catch (err) {
      console.error('Failed to import tours CSV', err)
      addToast({
        title: 'Import failed',
        description: err instanceof Error ? err.message : 'Could not parse or import the CSV file.'
      })
    } finally {
      setIsCsvImporting(false)
    }
  }

  const handleRecalculateAll = useCallback(async () => {
    setRecalculatingTotals(true)
    try {
      const result = await recalculateTourTotals(filterOperatorId === 'all' ? null : filterOperatorId)
      await loadOperators()
      addToast({
        variant: 'success',
        title: 'Tour totals recalculated',
        description: `Recalculated ${result.recalculatedTourCount} tour${result.recalculatedTourCount === 1 ? '' : 's'} across ${result.operatorCount} operator${result.operatorCount === 1 ? '' : 's'}.`
      })
    } catch (err) {
      console.error('Failed to recalculate tour totals', err)
      addToast({
        variant: 'error',
        title: 'Recalculation failed',
        description: 'Unable to recalculate persisted tour totals right now.'
      })
    } finally {
      setRecalculatingTotals(false)
    }
  }, [addToast, filterOperatorId, loadOperators])

  const handleEvaluateAllHikes = useCallback(async () => {
    if (!canEvaluateHikeDifficulties) {
      return
    }

    const confirmed = window.confirm(
      'Evaluate all hike difficulties? This switches every Popular Hike and Tour Operator hike to Calculated mode and replaces manual overrides.'
    )
    if (!confirmed) {
      return
    }

    setEvaluatingHikeDifficulties(true)
    try {
      const result = await evaluateAllHikeDifficulties()
      await loadOperators()
      addToast({
        variant: 'success',
        title: 'Hike difficulties evaluated',
        description: `Updated ${result.totalEvaluated} hike${result.totalEvaluated === 1 ? '' : 's'}: ${result.popularHikesEvaluated} popular and ${result.tourOperatorHikesEvaluated} tour operator.`
      })
    } catch (err) {
      console.error('Failed to evaluate hike difficulties', err)
      addToast({
        variant: 'error',
        title: 'Evaluation failed',
        description: 'Evaluation did not finish. Some ratings or modes may have changed; refresh and check them before retrying.'
      })
    } finally {
      setEvaluatingHikeDifficulties(false)
    }
  }, [addToast, canEvaluateHikeDifficulties, loadOperators])

  const handleDeleteAllTours = useCallback(async () => {
    const confirmed = window.confirm(
      'WARNING: This will permanently delete ALL tours from the database across every operator. Any current filters on this page will be ignored. This action cannot be undone.'
    )
    if (!confirmed) {
      return
    }

    const doubleConfirmed = window.confirm(
      'Final confirmation: delete all tours across every operator? Click OK to proceed.'
    )
    if (!doubleConfirmed) {
      return
    }

    setDeletingAllTours(true)
    try {
      const result = await deleteAllTours()
      setOperators((prev) => prev.map((operator) => ({ ...operator, tours: [] })))
      closeEditDialog()
      addToast({
        variant: 'success',
        title: 'Tours deleted',
        description: `Removed ${result.deletedTourCount} tour${result.deletedTourCount === 1 ? '' : 's'} across ${result.operatorCount} operator${result.operatorCount === 1 ? '' : 's'}.`
      })
    } catch (err) {
      console.error('Failed to delete tours in bulk', err)
      addToast({
        variant: 'error',
        title: 'Delete failed',
        description: 'Unable to delete tours right now.'
      })
    } finally {
      setDeletingAllTours(false)
    }
  }, [addToast, closeEditDialog])

  const editTourContent = selectedTour && editForm ? (
    <Card className="w-full shadow-md">
      <CardHeader>
        <CardTitle>Edit tour</CardTitle>
        <CardDescription>Update tour details, itinerary metrics, and hero images.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="rounded-xl border border-border/60 bg-muted/20 p-4">
          <p className="text-sm font-semibold text-foreground">{selectedTour.name}</p>
          <p className="text-xs uppercase tracking-wide text-muted-foreground">{selectedTour.operatorName}</p>
        </div>

        <div className="grid gap-3 md:grid-cols-3">
          <div className="rounded-xl border border-border/60 bg-muted/20 px-4 py-3">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Total distance</p>
            <p className="mt-1 text-lg font-semibold text-foreground"><MeasurementValue valueMetric={editComputedTotals?.distanceKm} kind="distance" unit={editForm.sourceMeasurementUnit} /></p>
          </div>
          <div className="rounded-xl border border-border/60 bg-muted/20 px-4 py-3">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Elevation gain</p>
            <p className="mt-1 text-lg font-semibold text-foreground"><MeasurementValue valueMetric={editComputedTotals?.elevationGainMeters} kind="elevation" unit={editForm.sourceMeasurementUnit} /></p>
          </div>
          <div className="rounded-xl border border-border/60 bg-muted/20 px-4 py-3">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Elevation loss</p>
            <p className="mt-1 text-lg font-semibold text-foreground"><MeasurementValue valueMetric={editComputedTotals?.elevationLossMeters} kind="elevation" unit={editForm.sourceMeasurementUnit} /></p>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">Totals are calculated automatically from the itinerary rows when you save.</p>

        <div className="grid gap-4 md:grid-cols-2">
          <label className="block space-y-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Measurement system</span>
            <select
              aria-label="Edit tour measurement system"
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
              value={editForm.sourceMeasurementUnit}
              onChange={(event) => setEditForm((prev) => (prev ? { ...prev, sourceMeasurementUnit: event.target.value as MeasurementUnit } : prev))}
            >
              <option value="Metric">Metric (km, m, kg)</option>
              <option value="Imperial">Imperial (mi, ft, lb)</option>
            </select>
          </label>
          <div>
            <label htmlFor="edit-tour-name" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Tour name</label>
            <Input
              id="edit-tour-name"
              value={editForm.name}
              onChange={(event) => setEditForm((prev) => (prev ? { ...prev, name: event.target.value } : prev))}
            />
          </div>
          <div>
            <label htmlFor="edit-tour-link" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Public link</label>
            <Input
              id="edit-tour-link"
              value={editForm.link}
              onChange={(event) => setEditForm((prev) => (prev ? { ...prev, link: event.target.value } : prev))}
              placeholder="https://example.com/tour"
            />
            {editForm.link.trim() && !/^https?:\/\//i.test(editForm.link.trim()) && (
              <p className="mt-1 flex items-center gap-1 text-xs text-amber-500">
                <AlertTriangle className="h-3 w-3" /> This is not a valid URL
              </p>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="edit-tour-description" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Tour description</label>
          <Textarea
            id="edit-tour-description"
            value={editForm.description}
            onChange={(event) => setEditForm((prev) => (prev ? { ...prev, description: event.target.value } : prev))}
            placeholder="Describe what makes this itinerary unique."
            maxLength={4000}
          />
          <p className="mt-1 text-xs text-muted-foreground">Shown after the generated distance and elevation summary. Leave blank to use the generic tour copy.</p>
        </div>

        <div className="grid gap-4 md:grid-cols-4">
          <div>
            <label htmlFor="edit-tour-difficulty-mode" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Difficulty mode</label>
            <select
              id="edit-tour-difficulty-mode"
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
              value={editForm.difficultyMode}
              onChange={(event) => setEditForm((prev) => (prev ? { ...prev, difficultyMode: event.target.value as TourFormState['difficultyMode'] } : prev))}
            >
              {editForm.difficultyMode === 'Legacy' && <option value="Legacy">Legacy (choose a mode)</option>}
              <option value="Calculated">Calculated</option>
              <option value="Manual">Manual</option>
            </select>
            <p className="mt-1 text-xs text-muted-foreground">Calculated ratings update from the itinerary and highest point.</p>
          </div>
          <div>
            <label htmlFor="edit-tour-difficulty-level" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Difficulty</label>
            <select
              id="edit-tour-difficulty-level"
              aria-label="Manual difficulty"
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-60"
              value={editForm.difficultyLevel}
              disabled={editForm.difficultyMode !== 'Manual'}
              onChange={(event) => setEditForm((prev) => (prev ? { ...prev, difficultyLevel: event.target.value as TourFormState['difficultyLevel'] } : prev))}
            >
              {HIKE_DIFFICULTY_OPTIONS.map((difficulty) => (
                <option key={difficulty} value={difficulty}>{formatHikeDifficulty(difficulty)}</option>
              ))}
            </select>
            {editForm.difficultyMode !== 'Manual' && (
              <p className="mt-1 text-xs text-muted-foreground">Effective rating: {formatHikeDifficulty(parseHikeDifficulty(selectedTour.difficultyLevel)) ?? 'Unrated'}</p>
            )}
          </div>
          <div>
            <label htmlFor="edit-tour-plan-generation-profile" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Training plan profile</label>
            <select
              id="edit-tour-plan-generation-profile"
              aria-label="Edit tour training plan profile"
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
              value={editForm.planGenerationProfile}
              onChange={(event) => setEditForm((prev) => (prev ? { ...prev, planGenerationProfile: event.target.value as TourFormState['planGenerationProfile'] } : prev))}
            >
              {TOUR_PLAN_GENERATION_PROFILES.map((profile) => (
                <option key={profile} value={profile}>{planGenerationProfileLabel(profile)}</option>
              ))}
            </select>
            <p className="mt-1 text-xs text-muted-foreground">Day hike removes the standard weekly distance and elevation floors.</p>
          </div>
          {selectedTour.difficultyCalculation && (
            <div className="md:col-span-4 rounded-lg border border-border/60 bg-muted/20 px-3 py-2 text-xs text-muted-foreground">
              Score {selectedTour.difficultyCalculation.totalScore ?? '—'} · {selectedTour.difficultyCalculation.formulaType ?? 'Unknown formula'} · {selectedTour.difficultyCalculation.hikingDays} hiking day{selectedTour.difficultyCalculation.hikingDays === 1 ? '' : 's'} · formula {selectedTour.difficultyCalculation.version}
            </div>
          )}
          <div>
            <label htmlFor="edit-tour-pack-weight" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Pack weight ({unitLabelFor('weight', editForm.sourceMeasurementUnit)})</label>
            <Input
              id="edit-tour-pack-weight"
              value={editForm.packWeight}
              onChange={(event) => setEditForm((prev) => (prev ? { ...prev, packWeight: event.target.value } : prev))}
            />
          </div>
          <div>
            <label htmlFor="edit-tour-highest-point" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Highest point ({unitLabelFor('elevation', editForm.sourceMeasurementUnit)})</label>
            <Input
              id="edit-tour-highest-point"
              value={editForm.highestPoint}
              onChange={(event) => setEditForm((prev) => (prev ? { ...prev, highestPoint: event.target.value } : prev))}
            />
          </div>
          <div>
            <label htmlFor="edit-tour-above-8k" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Above 8k ft?</label>
            <select
              id="edit-tour-above-8k"
              className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
              value={editForm.aboveEightThousandFeet}
              onChange={(event) => setEditForm((prev) => (prev ? { ...prev, aboveEightThousandFeet: event.target.value as TourFormState['aboveEightThousandFeet'] } : prev))}
            >
              <option value="">Unknown</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>
          <div>
            <label htmlFor="edit-tour-country" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Country</label>
            <Input
              id="edit-tour-country"
              value={editForm.country}
              onChange={(event) => setEditForm((prev) => (prev ? { ...prev, country: event.target.value } : prev))}
            />
          </div>
          <div>
            <label htmlFor="edit-tour-state" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">State or province</label>
            <Input
              id="edit-tour-state"
              value={editForm.stateOrProvince}
              onChange={(event) => setEditForm((prev) => (prev ? { ...prev, stateOrProvince: event.target.value } : prev))}
            />
          </div>
          <div>
            <label htmlFor="edit-tour-region" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Geographic region</label>
            <Input
              id="edit-tour-region"
              value={editForm.geographicRegion}
              onChange={(event) => setEditForm((prev) => (prev ? { ...prev, geographicRegion: event.target.value } : prev))}
            />
          </div>
          <div>
            <label htmlFor="edit-tour-city" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">City</label>
            <Input
              id="edit-tour-city"
              value={editForm.city}
              onChange={(event) => setEditForm((prev) => (prev ? { ...prev, city: event.target.value } : prev))}
            />
          </div>
          <div>
            <label htmlFor="edit-tour-protected-area" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Protected area</label>
            <Input
              id="edit-tour-protected-area"
              value={editForm.protectedArea}
              onChange={(event) => setEditForm((prev) => (prev ? { ...prev, protectedArea: event.target.value } : prev))}
            />
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Itinerary</p>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={addDayToEditForm}
                disabled={editForm.itinerary.length >= MAX_ITINERARY_DAYS || savingTourId === selectedTour.id}
              >
                <Plus className="mr-1 h-4 w-4" /> Add day
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => selectedTour && setEditForm(mapTourToForm(selectedTour, measurements.unit))}
                disabled={savingTourId === selectedTour.id}
              >
                Reset
              </Button>
            </div>
          </div>

          <div className="space-y-3">
            <div className="grid gap-3 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground md:grid-cols-[repeat(4,minmax(0,1fr))_auto]">
              <span>Day</span>
              <span>{unitLabelFor('distance', editForm.sourceMeasurementUnit)}</span>
              <span>Gain ({unitLabelFor('elevation', editForm.sourceMeasurementUnit)})</span>
              <span>Loss ({unitLabelFor('elevation', editForm.sourceMeasurementUnit)})</span>
              <span className="hidden text-right md:block">Actions</span>
            </div>
            {editForm.itinerary.map((day, index) => (
              <div key={`edit-day-${index}`} className="grid gap-3 md:grid-cols-[repeat(4,minmax(0,1fr))_auto]">
                <Input
                  type="number"
                  min={1}
                  max={MAX_ITINERARY_DAYS}
                  value={day.dayNumber}
                  onChange={(event) => handleEditFormChange(index, 'dayNumber', event.target.value)}
                />
                <MeasurementInput
                  kind="distance"
                  unit={editForm.sourceMeasurementUnit}
                  placeholder={unitLabelFor('distance', editForm.sourceMeasurementUnit)}
                  valueMetric={parseNumberOrNull(day.distanceKm)}
                  onValueMetricChange={(value) => handleEditFormChange(index, 'distanceKm', formMetricString(value))}
                />
                <MeasurementInput
                  kind="elevation"
                  unit={editForm.sourceMeasurementUnit}
                  placeholder={`Gain (${unitLabelFor('elevation', editForm.sourceMeasurementUnit)})`}
                  valueMetric={parseNumberOrNull(day.elevationGainMeters)}
                  onValueMetricChange={(value) => handleEditFormChange(index, 'elevationGainMeters', formMetricString(value))}
                />
                <MeasurementInput
                  kind="elevation"
                  unit={editForm.sourceMeasurementUnit}
                  placeholder={`Loss (${unitLabelFor('elevation', editForm.sourceMeasurementUnit)})`}
                  valueMetric={parseNumberOrNull(day.elevationLossMeters)}
                  onValueMetricChange={(value) => handleEditFormChange(index, 'elevationLossMeters', formMetricString(value))}
                />
                <div className="flex items-center justify-end">
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => removeDayFromEditForm(index)}
                    disabled={editForm.itinerary.length === 1 || savingTourId === selectedTour.id}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Hero images</p>
          <div className="flex flex-wrap gap-3">
            {(!selectedTour.heroImageUrls || selectedTour.heroImageUrls.length === 0) && (
              <p className="text-xs text-muted-foreground">No hero images yet.</p>
            )}
            {selectedTour.heroImageUrls?.map((url, index) => (
              <div key={url} className="group relative flex flex-col items-start gap-1">
                <img
                  src={url}
                  alt={`Hero ${index + 1}`}
                  className="h-20 w-32 rounded-md border border-border/60 object-cover"
                />
                <button
                  type="button"
                  className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-rose-500 text-white opacity-0 transition-opacity hover:bg-rose-600 group-hover:opacity-100 disabled:opacity-50"
                  title="Delete image"
                  disabled={deletingHeroImageUrl === url}
                  onClick={() => handleDeleteHeroImage(url)}
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex-1">
              <label htmlFor="edit-tour-hero-upload" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Upload new hero image</label>
              <input
                id="edit-tour-hero-upload"
                type="file"
                accept="image/*"
                className="mt-1 block w-full text-xs"
                disabled={uploadingHeroImage}
                onChange={async (event) => {
                  const file = event.target.files?.[0]
                  if (file) {
                    await handleUploadHeroImage(file)
                  }
                  event.target.value = ''
                }}
              />
              {uploadingHeroImage && <p className="mt-1 text-xs text-muted-foreground">Uploading…</p>}
            </div>
            {selectedTour.heroImageUrls && selectedTour.heroImageUrls.length > 0 && (
              <Button
                size="sm"
                variant="outline"
                disabled={regeneratingThumbnails}
                onClick={async () => {
                  if (!selectedTourId) return
                  setRegeneratingThumbnails(true)
                  try {
                    const result = await regenerateTourThumbnails(selectedTourId)
                    addToast({
                      variant: 'success',
                      title: 'Thumbnails regenerated',
                      description: `Processed: ${result.processed}, Skipped: ${result.skipped}, Failed: ${result.failed}`
                    })
                  } catch (err) {
                    console.error('Failed to regenerate thumbnails', err)
                    addToast({
                      variant: 'error',
                      title: 'Regeneration failed',
                      description: 'Unable to regenerate thumbnails. Please try again.'
                    })
                  } finally {
                    setRegeneratingThumbnails(false)
                  }
                }}
              >
                {regeneratingThumbnails ? (
                  <>
                    <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                    Regenerating...
                  </>
                ) : (
                  'Regenerate Thumbnails'
                )}
              </Button>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <Button onClick={() => handleSaveTour()} disabled={savingTourId === selectedTour.id || !editForm.name.trim()}>
            {savingTourId === selectedTour.id ? 'Saving…' : 'Save changes'}
          </Button>
          <Button variant="ghost" onClick={closeEditDialog} disabled={savingTourId === selectedTour.id}>
            Close
          </Button>
        </div>
      </CardContent>
    </Card>
  ) : null

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, index) => (
          <div key={index} className="h-28 animate-pulse rounded-2xl border border-border/50 bg-muted/40" />
        ))}
      </div>
    )
  }

  if (error) {
    return (
      <Card className="border border-rose-200 bg-rose-50/70">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-rose-700">
            <AlertTriangle className="h-5 w-5" />
            Unable to load tours
          </CardTitle>
          <CardDescription className="text-rose-600">{error}</CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={() => loadOperators()} variant="accent">
            Retry
          </Button>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-5">
      {editTourContent ? (
        <Dialog open={Boolean(editTourContent)} onOpenChange={(isOpen) => !isOpen && closeEditDialog()}>
          <DialogContent className="w-[min(96vw,960px)] max-h-[92vh] overflow-y-auto p-5 sm:p-7">
            <DialogTitle className="sr-only">Edit tour</DialogTitle>
            <DialogDescription className="sr-only">Use this form to update tour details, itinerary, and hero images.</DialogDescription>
            {editTourContent}
          </DialogContent>
        </Dialog>
      ) : null}

      <header className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Tours</p>
          <h1 className="text-3xl font-semibold text-foreground">Operator tour catalog</h1>
          <p className="text-sm text-muted-foreground">Browse every tour itinerary, recalculate persisted totals, and make scoped edits based on your role.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <input
            ref={csvInputRef}
            type="file"
            accept=".csv"
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0]
              if (file) void handleImportCsv(file)
            }}
          />
          {isElevated && (
            <Button
              variant="outline"
              size="sm"
              onClick={triggerCsvImport}
              disabled={isCsvImporting || loading}
              className="gap-2"
            >
              {isCsvImporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              Import CSV
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportCsv}
            disabled={isCsvExporting || loading || flattenedTours.length === 0 || recalculatingTotals || evaluatingHikeDifficulties}
            className="gap-2"
          >
            {isCsvExporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Export CSV
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void handleRecalculateAll()}
            disabled={recalculatingTotals || evaluatingHikeDifficulties || deletingAllTours || filteredTours.length === 0}
            className="gap-2"
          >
            {recalculatingTotals ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCcw className="h-4 w-4" />}
            Recalculate All
          </Button>
          {canEvaluateHikeDifficulties && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => void handleEvaluateAllHikes()}
              disabled={evaluatingHikeDifficulties || recalculatingTotals || deletingAllTours}
              className="gap-2"
            >
              {evaluatingHikeDifficulties ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCcw className="h-4 w-4" />}
              {evaluatingHikeDifficulties ? 'Evaluating…' : 'Evaluate all hikes'}
            </Button>
          )}
          {isElevated && (
            <Button
              variant="destructive"
              size="sm"
              onClick={() => void handleDeleteAllTours()}
              disabled={deletingAllTours || loading || flattenedTours.length === 0 || recalculatingTotals || evaluatingHikeDifficulties || regeneratingAllThumbnails}
              className="gap-2"
            >
              {deletingAllTours ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              Delete All
            </Button>
          )}
          {isElevated && (
            <Button
              variant="outline"
              disabled={regeneratingAllThumbnails || recalculatingTotals || evaluatingHikeDifficulties || deletingAllTours}
              onClick={async () => {
                if (!window.confirm('Regenerate thumbnails for ALL tour hero images? This may take a while.')) return
                setRegeneratingAllThumbnails(true)
                try {
                  const result = await regenerateAllTourThumbnails()
                  addToast({
                    variant: 'success',
                    title: 'All thumbnails regenerated',
                    description: `Processed: ${result.processed}, Skipped: ${result.skipped}, Failed: ${result.failed}`
                  })
                } catch (err) {
                  console.error('Failed to regenerate all thumbnails', err)
                  addToast({
                    variant: 'error',
                    title: 'Regeneration failed',
                    description: 'Unable to regenerate thumbnails. Please try again.'
                  })
                } finally {
                  setRegeneratingAllThumbnails(false)
                }
              }}
            >
              {regeneratingAllThumbnails ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Regenerating All...
                </>
              ) : (
                'Regenerate All Thumbnails'
              )}
            </Button>
          )}
          <div className="flex items-center gap-3 rounded-2xl border border-border bg-card px-4 py-3">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <select
              className="min-w-[200px] rounded-lg border border-border bg-background px-3 py-2 text-sm"
              value={filterOperatorId}
              onChange={(event) => setFilterOperatorId(event.target.value)}
            >
              <option value="all">All operators</option>
              {operators.map((operator) => (
                <option key={operator.id} value={operator.id}>
                  {operator.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Tours overview</CardTitle>
          <CardDescription>Showing {sortedTours.length} of {flattenedTours.length} tours</CardDescription>
          {readinessError ? <p className="text-xs text-amber-600">{readinessError}</p> : null}
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {sortedTours.length === 0 ? (
            <EmptyState
              icon={MapIcon}
              title="No tours in this filter"
              description="Try selecting a different operator or create a new tour."
            />
          ) : (
            <table className="w-full min-w-[1080px] text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="cursor-pointer select-none pb-3" onClick={() => requestSort('name')}>
                    <span className="inline-flex items-center gap-1">Tour {renderSortIndicator('name')}</span>
                  </th>
                  <th className="cursor-pointer select-none pb-3" onClick={() => requestSort('operatorName')}>
                    <span className="inline-flex items-center gap-1">Operator {renderSortIndicator('operatorName')}</span>
                  </th>
                  <th className="cursor-pointer select-none pb-3" onClick={() => requestSort('dayCount')}>
                    <span className="inline-flex items-center gap-1">Days {renderSortIndicator('dayCount')}</span>
                  </th>
                  <th className="pb-3">Plan profile</th>
                  <th className="cursor-pointer select-none pb-3 text-right" onClick={() => requestSort('totalDistanceKm')}>
                    <span className="inline-flex items-center gap-1">Dist ({measurements.unitLabel('distance')}) {renderSortIndicator('totalDistanceKm')}</span>
                  </th>
                  <th className="cursor-pointer select-none pb-3 text-right" onClick={() => requestSort('totalElevationGainMeters')}>
                    <span className="inline-flex items-center gap-1">Gain ({measurements.unitLabel('elevation')}) {renderSortIndicator('totalElevationGainMeters')}</span>
                  </th>
                  <th className="cursor-pointer select-none pb-3 text-right" onClick={() => requestSort('totalElevationLossMeters')}>
                    <span className="inline-flex items-center gap-1">Loss ({measurements.unitLabel('elevation')}) {renderSortIndicator('totalElevationLossMeters')}</span>
                  </th>
                  <th className="pb-3 text-right">Trip Readiness</th>
                  <th className="pb-3">Link</th>
                  <th className="pb-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedTours.map((tour) => {
                  const canManage = canManageTour(tour.operatorId)
                  const readiness = tourReadiness[tour.id]
                  return (
                    <tr key={tour.id} className="border-b border-border/60 last:border-b-0">
                      <td className="py-3 font-medium text-foreground">{tour.name}</td>
                      <td className="py-3 text-muted-foreground">{tour.operatorName}</td>
                      <td className="py-3 font-mono text-xs">{tour.itinerary.length}</td>
                      <td className="py-3">{planGenerationProfileLabel(tour.planGenerationProfile ?? DEFAULT_PLAN_GENERATION_PROFILE)}</td>
                      <td className="py-3 text-right font-mono text-xs"><MeasurementValue valueMetric={tour.totalDistanceKm} kind="distance" /></td>
                      <td className="py-3 text-right font-mono text-xs"><MeasurementValue valueMetric={tour.totalElevationGainMeters} kind="elevation" /></td>
                      <td className="py-3 text-right font-mono text-xs"><MeasurementValue valueMetric={tour.totalElevationLossMeters} kind="elevation" /></td>
                      <td className="py-3 text-right">
                        {readinessLoading && !readiness ? (
                          <span className="inline-flex items-center justify-end gap-1 text-xs text-muted-foreground">
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            Loading
                          </span>
                        ) : readiness?.tripReadinessScore != null ? (
                          <div className="space-y-0.5">
                            <p className="font-mono text-sm font-semibold text-foreground">{readiness.tripReadinessScore}</p>
                            <p className="text-xs text-muted-foreground">
                              {readiness.scoredTravelerCount}/{readiness.travelerCount} scored
                            </p>
                          </div>
                        ) : (
                          <div className="space-y-0.5 text-muted-foreground">
                            <p className="font-mono text-sm">--</p>
                            <p className="text-xs">{readiness?.groupId ? 'No scored travelers' : 'No group linked yet'}</p>
                          </div>
                        )}
                      </td>
                      <td className="py-3">
                        {tour.link ? (
                          /^https?:\/\//i.test(tour.link) ? (
                            <a href={tour.link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary underline">
                              View <ExternalLink className="h-3.5 w-3.5" />
                            </a>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs text-amber-500" title={tour.link}>
                              <AlertTriangle className="h-3 w-3" /> Not a URL
                            </span>
                          )
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="py-3 text-right">
                        <div className="flex justify-end gap-2">
                          <Button size="sm" variant="outline" disabled={!canManage} onClick={() => handleSelectTour(tour.id)}>
                            <Pencil className="mr-1 h-4 w-4" />
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-rose-600 hover:bg-rose-50"
                            disabled={!canManage || deletingTourId === tour.id}
                            onClick={() => handleDeleteTour(tour.id)}
                          >
                            {deletingTourId === tour.id ? 'Deleting…' : 'Delete'}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Create new tour</CardTitle>
          <CardDescription>Spin up a fresh itinerary for any operator.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isElevated ? (
            <div>
              <label htmlFor="new-tour-operator" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Operator</label>
              <select
                id="new-tour-operator"
                className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                value={newTourOperatorId ?? ''}
                onChange={(event) => setNewTourOperatorId(event.target.value || null)}
              >
                {operators.map((operator) => (
                  <option key={operator.id} value={operator.id}>
                    {operator.name}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <div className="rounded-xl border border-border/60 bg-muted/20 px-4 py-3 text-sm">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Operator</p>
              <p className="font-medium text-foreground">{operators.find((op) => op.id === user?.operatorId)?.name ?? 'Not linked'}</p>
            </div>
          )}

          <div className="grid gap-3 md:grid-cols-3">
            <div className="rounded-xl border border-border/60 bg-muted/20 px-4 py-3">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Total distance</p>
              <p className="mt-1 text-lg font-semibold text-foreground"><MeasurementValue valueMetric={newTourComputedTotals.distanceKm} kind="distance" unit={newTourForm.sourceMeasurementUnit} /></p>
            </div>
            <div className="rounded-xl border border-border/60 bg-muted/20 px-4 py-3">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Elevation gain</p>
              <p className="mt-1 text-lg font-semibold text-foreground"><MeasurementValue valueMetric={newTourComputedTotals.elevationGainMeters} kind="elevation" unit={newTourForm.sourceMeasurementUnit} /></p>
            </div>
            <div className="rounded-xl border border-border/60 bg-muted/20 px-4 py-3">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Elevation loss</p>
              <p className="mt-1 text-lg font-semibold text-foreground"><MeasurementValue valueMetric={newTourComputedTotals.elevationLossMeters} kind="elevation" unit={newTourForm.sourceMeasurementUnit} /></p>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">Totals are calculated automatically from the itinerary rows when you create the tour.</p>

          <Input
            placeholder="Tour name"
            value={newTourForm.name}
            onChange={(event) => setNewTourForm((prev) => ({ ...prev, name: event.target.value }))}
          />
          <Input
            placeholder="Public link (optional)"
            value={newTourForm.link}
            onChange={(event) => setNewTourForm((prev) => ({ ...prev, link: event.target.value }))}
          />
          {newTourForm.link.trim() && !/^https?:\/\//i.test(newTourForm.link.trim()) && (
            <p className="mt-1 flex items-center gap-1 text-xs text-amber-500">
              <AlertTriangle className="h-3 w-3" /> This is not a valid URL
            </p>
          )}
          <Textarea
            aria-label="Tour description"
            placeholder="Tour description (optional)"
            value={newTourForm.description}
            onChange={(event) => setNewTourForm((prev) => ({ ...prev, description: event.target.value }))}
            maxLength={4000}
          />
          <p className="text-xs text-muted-foreground">Shown after the generated distance and elevation summary. Leave blank to use the generic tour copy.</p>

          <div className="grid gap-4 md:grid-cols-4">
            <select
              aria-label="New tour measurement system"
              className="rounded-lg border border-border bg-background px-3 py-2 text-sm"
              value={newTourForm.sourceMeasurementUnit}
              onChange={(event) => setNewTourForm((prev) => ({ ...prev, sourceMeasurementUnit: event.target.value as MeasurementUnit }))}
            >
              <option value="Metric">Metric (km, m, kg)</option>
              <option value="Imperial">Imperial (mi, ft, lb)</option>
            </select>
            <select
              aria-label="Difficulty mode"
              className="rounded-lg border border-border bg-background px-3 py-2 text-sm"
              value={newTourForm.difficultyMode}
              onChange={(event) => setNewTourForm((prev) => ({ ...prev, difficultyMode: event.target.value as TourFormState['difficultyMode'] }))}
            >
              <option value="Calculated">Calculated difficulty</option>
              <option value="Manual">Manual difficulty</option>
            </select>
            <select
              aria-label="Manual difficulty"
              className="rounded-lg border border-border bg-background px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-60"
              value={newTourForm.difficultyLevel}
              disabled={newTourForm.difficultyMode !== 'Manual'}
              onChange={(event) => setNewTourForm((prev) => ({ ...prev, difficultyLevel: event.target.value as TourFormState['difficultyLevel'] }))}
            >
              {HIKE_DIFFICULTY_OPTIONS.map((difficulty) => (
                <option key={difficulty} value={difficulty}>{formatHikeDifficulty(difficulty)}</option>
              ))}
            </select>
            <label className="space-y-1">
              <span className="sr-only">Training plan profile</span>
              <select
                aria-label="New tour training plan profile"
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                value={newTourForm.planGenerationProfile}
                onChange={(event) => setNewTourForm((prev) => ({ ...prev, planGenerationProfile: event.target.value as TourFormState['planGenerationProfile'] }))}
              >
                {TOUR_PLAN_GENERATION_PROFILES.map((profile) => (
                  <option key={profile} value={profile}>{planGenerationProfileLabel(profile)}</option>
                ))}
              </select>
              <span className="block text-xs text-muted-foreground">Day hike removes weekly distance and elevation floors.</span>
            </label>
            <Input
              placeholder={`Pack weight (${unitLabelFor('weight', newTourForm.sourceMeasurementUnit)})`}
              value={newTourForm.packWeight}
              onChange={(event) => setNewTourForm((prev) => ({ ...prev, packWeight: event.target.value }))}
            />
            <Input
              placeholder={`Highest point (${unitLabelFor('elevation', newTourForm.sourceMeasurementUnit)})`}
              value={newTourForm.highestPoint}
              onChange={(event) => setNewTourForm((prev) => ({ ...prev, highestPoint: event.target.value }))}
            />
            <select
              className="rounded-lg border border-border bg-background px-3 py-2 text-sm"
              value={newTourForm.aboveEightThousandFeet}
              onChange={(event) => setNewTourForm((prev) => ({ ...prev, aboveEightThousandFeet: event.target.value as TourFormState['aboveEightThousandFeet'] }))}
              aria-label="Above 8k ft"
            >
              <option value="">Above 8k ft?</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
            <Input
              placeholder="Country"
              value={newTourForm.country}
              onChange={(event) => setNewTourForm((prev) => ({ ...prev, country: event.target.value }))}
            />
            <Input
              placeholder="State or province"
              value={newTourForm.stateOrProvince}
              onChange={(event) => setNewTourForm((prev) => ({ ...prev, stateOrProvince: event.target.value }))}
            />
            <Input
              placeholder="Geographic region"
              value={newTourForm.geographicRegion}
              onChange={(event) => setNewTourForm((prev) => ({ ...prev, geographicRegion: event.target.value }))}
            />
            <Input
              placeholder="City"
              value={newTourForm.city}
              onChange={(event) => setNewTourForm((prev) => ({ ...prev, city: event.target.value }))}
            />
            <Input
              placeholder="Protected area"
              value={newTourForm.protectedArea}
              onChange={(event) => setNewTourForm((prev) => ({ ...prev, protectedArea: event.target.value }))}
            />
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Itinerary</p>
              <Button size="sm" variant="outline" onClick={addDayToNewForm} disabled={newTourForm.itinerary.length >= MAX_ITINERARY_DAYS}>
                <Plus className="mr-1 h-4 w-4" /> Add day
              </Button>
            </div>
            <div className="grid gap-3 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground md:grid-cols-[repeat(4,minmax(0,1fr))_auto]">
              <span>Day</span>
              <span>{unitLabelFor('distance', newTourForm.sourceMeasurementUnit)}</span>
              <span>Gain ({unitLabelFor('elevation', newTourForm.sourceMeasurementUnit)})</span>
              <span>Loss ({unitLabelFor('elevation', newTourForm.sourceMeasurementUnit)})</span>
              <span className="hidden md:block text-right">Actions</span>
            </div>
            {newTourForm.itinerary.map((day, index) => (
              <div key={`new-day-${index}`} className="grid gap-3 md:grid-cols-[repeat(4,minmax(0,1fr))_auto]">
                <Input
                  type="number"
                  min={1}
                  max={MAX_ITINERARY_DAYS}
                  value={day.dayNumber}
                  onChange={(event) => handleNewFormChange(index, 'dayNumber', event.target.value)}
                />
                <MeasurementInput
                  kind="distance"
                  unit={newTourForm.sourceMeasurementUnit}
                  placeholder={unitLabelFor('distance', newTourForm.sourceMeasurementUnit)}
                  valueMetric={parseNumberOrNull(day.distanceKm)}
                  onValueMetricChange={(value) => handleNewFormChange(index, 'distanceKm', formMetricString(value))}
                />
                <MeasurementInput
                  kind="elevation"
                  unit={newTourForm.sourceMeasurementUnit}
                  placeholder={`Gain (${unitLabelFor('elevation', newTourForm.sourceMeasurementUnit)})`}
                  valueMetric={parseNumberOrNull(day.elevationGainMeters)}
                  onValueMetricChange={(value) => handleNewFormChange(index, 'elevationGainMeters', formMetricString(value))}
                />
                <MeasurementInput
                  kind="elevation"
                  unit={newTourForm.sourceMeasurementUnit}
                  placeholder={`Loss (${unitLabelFor('elevation', newTourForm.sourceMeasurementUnit)})`}
                  valueMetric={parseNumberOrNull(day.elevationLossMeters)}
                  onValueMetricChange={(value) => handleNewFormChange(index, 'elevationLossMeters', formMetricString(value))}
                />
                <div className="flex items-center justify-end">
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => removeDayFromNewForm(index)}
                    disabled={newTourForm.itinerary.length === 1}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-3">
            <Button onClick={() => handleCreateTour()} disabled={creatingTour || !newTourForm.name.trim()}>
              {creatingTour ? 'Creating…' : 'Create tour'}
            </Button>
            <Button variant="ghost" onClick={() => setNewTourForm(createEmptyTourForm(measurements.unit))} disabled={creatingTour}>
              Reset form
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
